import * as XLSX from "xlsx";

export interface ExportData {
  users?: Array<{
    name: string;
    email: string;
    campaigns: number;
    feedbackGiven: number;
    totalEngagement: number;
    joined: string;
    status: string;
  }>;
  campaigns?: Array<{
    title: string;
    creator: string;
    status: string;
    responses: number;
    comments: number;
    created: string;
    category: string;
  }>;
  analytics?: {
    userGrowth?: Array<{
      date: string;
      newUsers: number;
      totalUsers: number;
    }>;
    campaignGrowth?: Array<{
      date: string;
      created: number;
      published: number;
      completed: number;
    }>;
    engagement?: Array<{
      date: string;
      responses: number;
      comments: number;
      likes: number;
    }>;
  };
  activity?: Array<{
    type: string;
    description: string;
    user: string;
    campaign: string;
    createdAt: string;
  }>;
}

export interface ExportOptions {
  filename?: string;
  includeUsers?: boolean;
  includeCampaigns?: boolean;
  includeAnalytics?: boolean;
  includeActivity?: boolean;
  dateRange?: {
    start: Date;
    end: Date;
  };
}

export const exportDashboardData = (
  data: ExportData,
  options: ExportOptions = {}
): void => {
  const {
    filename = `admin-dashboard-export-${
      new Date().toISOString().split("T")[0]
    }`,
    includeUsers = true,
    includeCampaigns = true,
    includeAnalytics = true,
    includeActivity = true,
  } = options;

  const workbook = XLSX.utils.book_new();

  // Export Users Data
  if (includeUsers && data.users && data.users.length > 0) {
    const usersWorksheet = XLSX.utils.json_to_sheet(data.users);
    XLSX.utils.book_append_sheet(workbook, usersWorksheet, "Users");
  }

  // Export Campaigns Data
  if (includeCampaigns && data.campaigns && data.campaigns.length > 0) {
    const campaignsWorksheet = XLSX.utils.json_to_sheet(data.campaigns);
    XLSX.utils.book_append_sheet(workbook, campaignsWorksheet, "Campaigns");
  }

  // Export Analytics Data
  if (includeAnalytics && data.analytics) {
    if (data.analytics.userGrowth && data.analytics.userGrowth.length > 0) {
      const userGrowthWorksheet = XLSX.utils.json_to_sheet(
        data.analytics.userGrowth
      );
      XLSX.utils.book_append_sheet(
        workbook,
        userGrowthWorksheet,
        "User Growth"
      );
    }

    if (
      data.analytics.campaignGrowth &&
      data.analytics.campaignGrowth.length > 0
    ) {
      const campaignGrowthWorksheet = XLSX.utils.json_to_sheet(
        data.analytics.campaignGrowth
      );
      XLSX.utils.book_append_sheet(
        workbook,
        campaignGrowthWorksheet,
        "Campaign Growth"
      );
    }

    if (data.analytics.engagement && data.analytics.engagement.length > 0) {
      const engagementWorksheet = XLSX.utils.json_to_sheet(
        data.analytics.engagement
      );
      XLSX.utils.book_append_sheet(workbook, engagementWorksheet, "Engagement");
    }
  }

  // Export Activity Data
  if (includeActivity && data.activity && data.activity.length > 0) {
    const activityWorksheet = XLSX.utils.json_to_sheet(data.activity);
    XLSX.utils.book_append_sheet(workbook, activityWorksheet, "Activity Log");
  }

  // Add summary sheet
  const summaryData = [
    { Metric: "Total Users", Value: data.users?.length || 0 },
    { Metric: "Total Campaigns", Value: data.campaigns?.length || 0 },
    { Metric: "Total Activities", Value: data.activity?.length || 0 },
    { Metric: "Export Date", Value: new Date().toLocaleString() },
    {
      Metric: "Data Range",
      Value: options.dateRange
        ? `${options.dateRange.start.toLocaleDateString()} - ${options.dateRange.end.toLocaleDateString()}`
        : "All Time",
    },
  ];

  const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summaryWorksheet, "Summary");

  // Download the file
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

export const exportToCSV = (
  data: Record<string, unknown>[],
  filename: string
): void => {
  if (data.length === 0) return;

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(","),
    ...data.map((row) =>
      headers
        .map((header) => {
          const value = row[header];
          // Escape commas and quotes in CSV
          if (
            typeof value === "string" &&
            (value.includes(",") || value.includes('"'))
          ) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const formatDateForExport = (date: string | Date): string => {
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatNumberForExport = (num: number): string => {
  return num.toLocaleString();
};
